import React, { useEffect, useState } from "react";
import { Card } from "reactstrap";
import { SearchIcon } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CommonTable from "../CommonTable";
import { useLoader } from "../../_helper/LoaderContext";
import { useLab } from "../../_helper/PharmacyProvider";
import DatePicker from "../Common/Component/DatePicker";
import { formatDoctorDisplayDateTime, toApiDateString } from "../../utils/dateTime/dateFormat";
import SampleDetailModal from "./SampleDetailModal";
import BarcodePreviewModal, { getCollectedBarcodeIds } from "./BarcodePreviewModal";
import CollectSamplesModal from "../TestReport/CollectSamplesModal";
import { getTestResult } from "../TestReport/service/report-test-service";
import {
    SAMPLE_COLLECTION_STATUSES,
    SAMPLE_COLLECTION_UPDATE_STATUSES,
    canCollectStatus,
    canRecollectStatus,
    collectSample,
    getSampleCollectionList,
    updateSampleCollectionStatus,
    viewSampleByBarcode,
} from "./service/sample-collection-service";

const statusSelectStyle = (status) => {
    const value = (status || "").toLowerCase();
    if (value === "collected" || value === "completed") {
        return {
            background: "#198754",
            color: "#ffffff",
            borderColor: "#146c43",
        };
    }
    if (value === "pending") {
        return {
            background: "#ffc107",
            color: "#212529",
            borderColor: "#e0a800",
        };
    }
    if (value === "rejected" || value === "cancelled" || value === "Re-Collected") {
        return {
            background: "#dc3545",
            color: "#ffffff",
            borderColor: "#b02a37",
        };
    }
    return {
        background: "#6c757d",
        color: "#ffffff",
        borderColor: "#5c636a",
    };
};

export default function SampleCollectionHome() {
    const navigate = useNavigate();
    const { setLoading } = useLoader();
    const { currentLab } = useLab();
    const [samples, setSamples] = useState([]);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [barcodeQuery, setBarcodeQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedId, setSelectedId] = useState(null);
    const [modalInitial, setModalInitial] = useState(null);
    const [barcodePreviewRow, setBarcodePreviewRow] = useState(null);
    const [showRecollect, setShowRecollect] = useState(false);
    const [recollectOrder, setRecollectOrder] = useState(null);
    const [recollectTarget, setRecollectTarget] = useState(null);

    const showAllEquipment = (equipmentList = []) => {
        const names = equipmentList.map((item) => item?.name).filter(Boolean);
        const listHtml = names
            .map(
                (name) => `
                <div style="
                    display:inline-flex;
                    align-items:center;
                    margin:4px;
                    padding:7px 14px;
                    border-radius:6px;
                    background:#1b255b;
                    color:#ffffff;
                    font-size:13px;
                    font-weight:600;
                ">
                    ${name}
                </div>
            `
            )
            .join("");

        Swal.fire({
            title: `<div style="font-size:18px;font-weight:600;color:#1b255b;">Assigned Equipment</div>
                    <div style="font-size:13px;color:#6b7280;margin-top:4px;">${names.length} item${names.length > 1 ? "s" : ""}</div>`,
            html: `<div style="display:flex;flex-wrap:wrap;justify-content:center;padding:8px 4px;">${listHtml || "<div>No equipment</div>"}</div>`,
            confirmButtonText: "Close",
            confirmButtonColor: "#1b255b",
            width: "480px",
        });
    };

    const formatEquipmentDisplay = (equipmentList) => {
        if (!Array.isArray(equipmentList) || equipmentList.length === 0) {
            return <span style={{ color: "#9ca3af" }}>—</span>;
        }

        const firstName = equipmentList[0]?.name || "—";
        const moreCount = equipmentList.length - 1;

        return (
            <div className="d-flex align-items-center flex-wrap gap-2">
                <span title={firstName}>{firstName}</span>
                {moreCount > 0 && (
                    <button
                        type="button"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "5px 10px",
                            borderRadius: "6px",
                            background: "#0d6efd",
                            color: "#ffffff",
                            fontSize: "12px",
                            fontWeight: 700,
                            border: "none",
                            cursor: "pointer",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            showAllEquipment(equipmentList);
                        }}
                    >
                        +{moreCount} more
                    </button>
                )}
            </div>
        );
    };

    const getList = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("search", search);
            formData.append("page", currentPage);
            formData.append("limit", entriesPerPage);
            formData.append("sortBy", "createdAt");
            formData.append("sortOrder", "desc");
            if (status) formData.append("status", status);
            const apiStartDate = toApiDateString(startDate);
            const apiEndDate = toApiDateString(endDate);
            if (apiStartDate) formData.append("startDate", apiStartDate);
            if (apiEndDate) formData.append("endDate", apiEndDate);

            const res = await getSampleCollectionList(formData);
            if (res.data.success === true) {
                const payload = res.data.data;
                setSamples(payload?.samples || []);
                setTotalEntries(payload?.total || 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            getList();
        }, 500);
        return () => clearTimeout(delayDebounce);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, currentPage, entriesPerPage, status, startDate, endDate]);

    const handleStatusChange = (row, newStatus) => {
        if (row.status === "Re-Collected") return;
        if (!newStatus || newStatus === row.status) return;

        const needsReason = newStatus === "Rejected" || newStatus === "Cancelled";

        const swalConfig = needsReason
            ? {
                title: `${newStatus} sample?`,
                html: `<p style="margin-bottom:8px;">Update status from "<strong>${row.status || "-"}</strong>" to "<strong>${newStatus}</strong>"?</p>`,
                input: "textarea",
                inputLabel: "Reason",
                inputPlaceholder: `Enter reason for ${newStatus.toLowerCase()}...`,
                inputValidator: (value) => {
                    if (!value?.trim()) return "Reason is required";
                    return null;
                },
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc3545",
                cancelButtonColor: "#6c757d",
                confirmButtonText: `Yes, ${newStatus.toLowerCase()}`,
            }
            : {
                title: "Change status?",
                text: `Update status from "${row.status || "-"}" to "${newStatus}"?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, update",
            };

        Swal.fire(swalConfig).then(async (result) => {
            if (!result.isConfirmed) return;

            setLoading(true);
            try {
                const payload = { status: newStatus };
                if (newStatus === "Collected") {
                    payload.collectionTime = new Date().toISOString();
                }
                if (needsReason && result.value?.trim()) {
                    payload.notes = result.value.trim();
                }

                const res = await updateSampleCollectionStatus(row._id, payload);
                if (res.data.success === true) {
                    toast.success(res.data.message || "Status updated successfully");
                    getList();
                } else {
                    toast.warning(res.data.message || "Unable to update status");
                }
            } catch (error) {
                console.log(error);
                toast.warning(error?.response?.data?.message || "Unable to update status");
            } finally {
                setLoading(false);
            }
        });
    };

    const openModal = (mode, row = null, initial = null) => {
        setModalMode(mode);
        setSelectedId(row?._id || initial?._id || null);
        setModalInitial(initial);
        setModalOpen(true);
    };

    const handleViewOrderSamples = (row) => {
        const patientId = row?.patientId;
        if (!patientId) {
            toast.warning("Patient not found for this sample");
            return;
        }
        navigate(`${process.env.PUBLIC_URL}/sample-collection/${patientId}`, {
            state: {
                patientId,
                testOrderId: row?.testOrderId || "",
                sampleId: row?._id || "",
            },
        });
    };

    const handleCollect = (row) => {
        if (!canCollectStatus(row.status)) {
            toast.warning("Only Pending samples can be collected.");
            return;
        }

        Swal.fire({
            title: "Collect sample?",
            text: `Mark barcode ${row.barcodeId || ""} as Collected?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#198754",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, collect",
        }).then(async (result) => {
            if (!result.isConfirmed) return;

            setLoading(true);
            try {
                const res = await collectSample(row._id, {
                    collectionTime: new Date().toISOString(),
                });
                if (res.data.success === true) {
                    toast.success(res.data.message || "Sample collected successfully.");
                    getList();
                } else {
                    toast.warning(res.data.message || "Unable to collect sample");
                }
            } catch (error) {
                console.log(error);
                toast.warning(error?.response?.data?.message || "Unable to collect sample");
            } finally {
                setLoading(false);
            }
        });
    };

    const handleRecollect = async (row) => {
        if (!canRecollectStatus(row.status)) {
            toast.warning("Only Rejected or Cancelled samples can be recollected.");
            return;
        }
        if (!row.testOrderId) {
            toast.warning("Test order not found");
            return;
        }

        setLoading(true);
        let view = null;
        try {
            const res = await getTestResult(row.testOrderId);
            if (res.data?.success) {
                view = res.data.data || null;
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }

        const allTests = (view?.allTests || [])
            .map((test) => ({
                testId: test.testId || test._id,
                name: test.name || test.testName || "Test",
                sampleTypeId: test.sampleTypeId,
                sampleTypeName: test.sampleTypeName,
                samples: Array.isArray(test.samples) ? [...test.samples] : [],
                assignedEquipment: test.assignedEquipment || [],
            }))
            .filter((test) => test.testId);

        if (row.testId && !allTests.some((test) => test.testId === row.testId)) {
            allTests.push({
                testId: row.testId,
                name: row.testName || "Test",
                sampleTypeId: row.sampleId,
                sampleTypeName: row.sampleName,
                samples: [],
                assignedEquipment: row.assignedEquipment || [],
            });
        }

        const matchedTest = allTests.find((test) => test.testId === row.testId);
        if (
            matchedTest &&
            row.sampleId &&
            !matchedTest.samples.some((sample) => (sample.sampleId || sample._id) === row.sampleId)
        ) {
            matchedTest.samples.push({
                sampleId: row.sampleId,
                name: row.sampleName || "Sample",
                description: row.sampleDescription || "",
                assignedEquipment: row.assignedEquipment || [],
            });
        }

        setRecollectOrder({
            _id: row.testOrderId,
            orderBarcodeId: row.orderBarcodeId || view?.orderBarcodeId || "",
            patient: { name: row.patientName || view?.patient?.name || "—" },
            allTests,
        });
        setRecollectTarget(row);
        setShowRecollect(true);
    };

    const handleBarcodeLookup = async () => {
        const barcodeId = barcodeQuery.trim();
        if (!barcodeId) {
            toast.info("Enter a barcode ID to look up");
            return;
        }

        setLoading(true);
        try {
            const res = await viewSampleByBarcode(barcodeId);
            if (res.data.success === true) {
                openModal("barcode", null, res.data.data);
            } else {
                toast.warning(res.data.message || "Sample not found for this barcode");
            }
        } catch (error) {
            console.log(error);
            toast.warning(error?.response?.data?.message || "Sample not found for this barcode");
        } finally {
            setLoading(false);
        }
    };

    const actions = [
        { label: "View", onClick: (row) => handleViewOrderSamples(row) },
        { label: "Edit", onClick: (row) => openModal("edit", row) },
        {
            label: "Barcode",
            onClick: (row) => {
                if (getCollectedBarcodeIds(row).length === 0) {
                    toast.warning("Barcode ID not available");
                    return;
                }
                setBarcodePreviewRow(row);
            },
        },
        {
            label: "Collect",
            onClick: (row) => handleCollect(row),
            show: (row) => canCollectStatus(row.status),
        },
        {
            label: "Recollect",
            onClick: (row) => handleRecollect(row),
            show: (row) => canRecollectStatus(row.status),
        },
    ];

    const columns = [
        {
            header: "Order Barcode",
            accessor: "orderBarcodeId",
            width: "150px",
            render: (value) => (
                <span className="fw-semibold" style={{ fontSize: "12px" }}>
                    {value || "—"}
                </span>
            ),
        },
        {
            header: "Patient",
            accessor: "patientName",
            width: "140px",
            render: (value, row) => (
                <div>
                    <div className="fw-semibold">{value || "—"}</div>
                    <small className="text-muted">{row.mobileNumber || ""}</small>
                </div>
            ),
        },
        { header: "Test", accessor: "testName", width: "130px" },        
        { header: "Sample", accessor: "sampleName", width: "110px" },
        {
            header: "Equipment",
            accessor: "assignedEquipment",
            width: "140px",
            render: (value) => formatEquipmentDisplay(value),
        },
        {
            header: "Collected At",
            accessor: "collectionTime",
            width: "130px",
            render: (value) => formatDoctorDisplayDateTime(value, currentLab) || "—",
        },
        {
            header: "Status",
            accessor: "status",
            width: "130px",
            render: (value, row) => {
                const colors = statusSelectStyle(value);
                const isRecollected = value === "Re-Collected";
                return (
                    <select
                        value={value || ""}
                        disabled={isRecollected}
                        onChange={(e) => handleStatusChange(row, e.target.value)}
                        style={{
                            minWidth: "118px",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: isRecollected ? "not-allowed" : "pointer",
                            outline: "none",
                            border: `1px solid ${colors.borderColor}`,
                            background: colors.background,
                            color: colors.color,
                        }}
                    >
                        {!SAMPLE_COLLECTION_UPDATE_STATUSES.includes(value) && value ? (
                            <option value={value}>{value}</option>
                        ) : null}
                        {SAMPLE_COLLECTION_UPDATE_STATUSES.map((option) => (
                            <option
                                key={option}
                                value={option}
                                style={{ background: "#ffffff", color: "#212529" }}
                            >
                                {option}
                            </option>
                        ))}
                    </select>
                );
            },
        },
    ];

    const totalPages = Math.ceil(totalEntries / entriesPerPage) || 1;

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="row g-3 align-items-end mb-3">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text tw-bg-white" id="sample-collection-search">
                                    <SearchIcon size={18} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control icon_form"
                                    placeholder="Search patient, test, sample, notes..."
                                    aria-describedby="sample-collection-search"
                                    style={{ borderLeft: "none", minHeight: "42px" }}
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <select
                                className="form-control"
                                style={{ minHeight: "42px" }}
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="">All Status</option>
                                {SAMPLE_COLLECTION_STATUSES.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small mb-1">Start Date</label>
                            <DatePicker
                                name="startDate"
                                value={startDate}
                                onChange={(event) => {
                                    setStartDate(event.target.value || "");
                                    setCurrentPage(1);
                                }}
                                isClearable
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small mb-1">End Date</label>
                            <DatePicker
                                name="endDate"
                                value={endDate}
                                onChange={(event) => {
                                    setEndDate(event.target.value || "");
                                    setCurrentPage(1);
                                }}
                                isClearable
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={samples}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}
                                setEntriesPerPage={setEntriesPerPage}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <BarcodePreviewModal
                show={Boolean(barcodePreviewRow)}
                onHide={() => setBarcodePreviewRow(null)}
                row={barcodePreviewRow}
            />

            <CollectSamplesModal
                show={showRecollect}
                onHide={() => {
                    setShowRecollect(false);
                    setRecollectTarget(null);
                }}
                order={recollectOrder}
                redirectOnSuccess={false}
                recollectSampleId={recollectTarget?._id || null}
                recollectSample={recollectTarget}
                onSuccess={() => getList()}
            />

            <SampleDetailModal
                show={modalOpen}
                onHide={() => {
                    setModalOpen(false);
                    setSelectedId(null);
                    setModalInitial(null);
                }}
                sampleId={selectedId}
                initialData={modalInitial}
                mode={modalMode}
                onUpdated={() => getList()}
            />
        </>
    );
}
